import { Tabs, Tab, Row, Col, Form, Button, InputGroup, Placeholder } from 'react-bootstrap'
import React, { useState, useRef } from 'react'
import { useParams } from 'react-router'
import './ProjectCard.scss'
import MediaSidebar from '@/components/MediaSidebar'
import './ProjectDetails.scss'
import AttachmentsList from '@/components/shared/AttachmentsList'
import QuestionsAnswersControl, { QuestionAnswerControlRefProps } from '@/components/QuestionsAnswersControl'
import RecordActionButtons from '@/components/RecordActionButtons'
import { RecordType } from '@/components/shared/DeleteRecordModal'
import ProjectInterviewsSelect from '@/components/ProjectInterviewsSelect'
import { trackEvent } from '@/utils/analytics'
import { useGetProjectQuery } from '@/features/apiSliceWithProjects'
import { Project } from '@/types'
import useToastQuery from '@/hooks/useToastQuery'

const DEFAULT_TAB = 'questionsAnswers'

export default function ProjectDetails() {
  const { projectId: projectIdParam } = useParams()
  const projectId = projectIdParam ? +projectIdParam : -1
  const { data, isFetching } = useToastQuery(useGetProjectQuery, projectId)

  const [activeTab, setActiveTab] = useState(DEFAULT_TAB)
  const isInlineQAListVisible = activeTab !== 'questionsAnswers'
  const [question, setQuestion] = useState<string>()
  const questionAnswerRef = useRef<QuestionAnswerControlRefProps>(null)

  if (isFetching || !data) return <ProjectDetailsSkeleton />
  const project = data as Required<Project>

  const hasMedia = project?.recording || (project?.photos?.length ?? 0) > 0
  const hasMediaOrAttachments = hasMedia || project?.attachments?.length

  return (
    <Row className="ProjectDetails justify-content-center g-4">
      {/* Main content (tabs) */}
      <Col xs={12} lg={8} className="order-2 order-lg-1">
        <div className="d-flex justify-content-between align-items-baseline">
          <h3 className="my-4">{project?.title}</h3>
          <RecordActionButtons
            showDetailsButton={false}
            idOrSlug={project.id!}
            recordType={RecordType.Project}
          />
        </div>
        <Tabs
          activeKey={activeTab}
          defaultActiveKey={DEFAULT_TAB}
          id="project-tabs"
          className="mb-3"
          onSelect={(tab) => {
            setActiveTab(tab || DEFAULT_TAB)
            trackEvent('project_tab_change', { tab })
          }}
        >
          <Tab
            eventKey={DEFAULT_TAB}
            title="Questions"
          >
            {project &&
              <QuestionsAnswersControl
                project={project}
                ref={questionAnswerRef}
              />
            }
          </Tab>

          <Tab
            className="ProjectDetails__Summary"
            eventKey="summary"
            title="Summary"
          >
            <ul className="d-flex flex-column gap-3 mt-4">
              {project?.summary?.map((sentence, idx) => (
                <li key={idx}>{sentence}</li>
              ))}
            </ul>
          </Tab>

          <Tab
            className="ProjectDetails__Transcript"
            eventKey="transcript"
            title="Transcript"
          >
            <ProjectInterviewsSelect
              value={project.interviews}
              options={project.interviews}
            />
          </Tab>

        </Tabs>
        {isInlineQAListVisible &&
          <Form
            onSubmit={e => {
              e.preventDefault()
              questionAnswerRef?.current?.submitQuestion(question as string)
              setActiveTab('questionsAnswers')
              setQuestion(undefined)
            }}
          >
            <InputGroup className="mt-5">
              <Form.Control
                placeholder="Ask a question"
                value={question}
                onChange={e => setQuestion(e.target.value)}
                required
              />
              <Button
                type="submit"
                className="d-flex align-items-center justify-content-center"
                aria-label="Toggle order"
              >
                Submit
              </Button>
            </InputGroup>
          </Form>
        }
      </Col>

      {/* Right sidebar (audio + photos) */}
      {hasMediaOrAttachments && (
        <Col xs={12} lg={3} className="ProjectDetails__SidebarColumn order-1 order-lg-2">
          {hasMedia &&
            <MediaSidebar
              audioUrl={project?.recording?.url}
              imageUrls={project?.photos?.map(photo => photo.url) ?? []}
              className="w-100"
            />}
          {project.attachments &&
            <AttachmentsList
              title="Attachments"
              attachments={project.attachments}
              className="mt-5"
            />
          }
        </Col>
      )}
    </Row>
  )
}

function ProjectDetailsSkeleton() {
  return (
    <Row className="ProjectDetails justify-content-center g-4">
      {/* Main content (tabs) */}
      <Col xs={12} lg={8} className="order-2 order-lg-1">
        {/* Title with action buttons */}
        <div className="d-flex justify-content-between align-items-baseline">
          <Placeholder as="h3" animation="wave" className="my-4">
            <Placeholder xs={7} />
          </Placeholder>
          <div className="d-flex gap-2">
            <Placeholder animation="wave">
              <Placeholder xs={3} style={{ height: '38px' }} />
            </Placeholder>
            <Placeholder animation="wave">
              <Placeholder xs={3} style={{ height: '38px' }} />
            </Placeholder>
          </div>
        </div>

        {/* Tabs navigation */}
        <div className="mb-3 pb-2 border-bottom">
          <Placeholder as="div" animation="wave" className="d-flex gap-3">
            <Placeholder xs={2} />
            <Placeholder xs={2} />
            <Placeholder xs={2} />
          </Placeholder>
        </div>

        {/* Search/Input form */}
        <div className="mb-4">
          <Placeholder as="div" animation="wave" className="d-flex gap-2">
            <Placeholder xs={9} style={{ height: '38px' }} />
            <Placeholder xs={1} style={{ height: '38px' }} />
            <Placeholder xs={1} style={{ height: '38px' }} />
            <Placeholder xs={1} style={{ height: '38px' }} />
          </Placeholder>
        </div>

        {/* Question items list */}
        <div className="d-flex flex-column gap-3">
          {[...Array(7)].map((_, i) => (
            <Placeholder as="div" animation="wave" key={i}
                         className="p-3 border rounded d-flex justify-content-between align-items-center">
              <Placeholder xs={6} />
              <Placeholder xs={1} />
            </Placeholder>
          ))}
        </div>
      </Col>

      {/* Right sidebar (audio + photos) */}
      <Col xs={12} lg={3} className="ProjectDetails__SidebarColumn order-1 order-lg-2">
        <Placeholder as="div" animation="wave" className="rounded" style={{ height: '300px' }} />
      </Col>
    </Row>
  )
}
